import { useState, useEffect, useRef } from 'react'; // Import React and hooks
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate for navigation

import { ArrowBack, Home } from '@mui/icons-material'; // Import Material UI icon

import logo from '../../assets/images/ctr-icn.png'; // Importer l'image du logo

import CustomModal from './custom-modal';
import InfoBox from './messages/info-box';
import { useMediaContext } from '../../context/media-context';

/**
 * Header Component
 * 
 * This component displays the page header including the title.
 *
 * @param {Object} props - The properties for the component.
 * @param {string} props.title - The title to be displayed in the header.
 */
const Header = ({ title }) => {
    const navigate = useNavigate(); // Use the useNavigate hook
    const location = useLocation(); // Use the useLocation hook to get the current location

    const { unsentMedia } = useMediaContext(); // Get unsent media state from context

    const messages = [
        "Bienvenue sur la plateforme CTR Drako",
        "Application ERP – Gestion centralisée de vos opérations",
        "Connexion sécurisée",
        "CTR Drako – L'efficacité au cœur de votre organisation",
    ];

    const joinedMessages = messages.join(' • ');

    const [messageIndex, setMessageIndex] = useState(0);
    const messageRef = useRef(null);

    const [isConfirmBackOpen, setIsConfirmBackOpen] = useState(false); // State for confirmation modal

    // Mise à jour du message après chaque animation
    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % messages.length);
        }, 7000); // Doit correspondre à la durée de l'animation
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Déclenche une re-animation en réappliquant la classe
        if (messageRef.current) {
            messageRef.current.classList.remove('scrolling-text');
            void messageRef.current.offsetWidth; // force reflow
            messageRef.current.classList.add('scrolling-text');
        }
    }, [messageIndex]);

    /**
     * handleBack
     * 
     * If on a media page with unsent media, opens a confirmation modal.
     * Otherwise navigates to parent route.
     */
    const handleBack = () => {
        // Determine if current route is one of the media upload pages by segment list
        const segments = location.pathname.split('/').filter(Boolean);
        const mediaPages = ['add-media-only', 'media-upload-tool'];
        const isMediaPage = segments[0] === 'folders' && mediaPages.includes(segments[2]);

        if (isMediaPage && unsentMedia) {
            setIsConfirmBackOpen(true);
        } else {
            navigateToParent();
        }
    };

    // Navigate to parent route
    const navigateToParent = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        pathSegments.pop();

        if (pathSegments[pathSegments.length - 1] === 'folders') {
            pathSegments.pop();
        }
        const newPath = '/' + pathSegments.join('/');
        navigate(newPath || '/');
    };

    // Confirm and navigate back
    const confirmBack = () => {
        setIsConfirmBackOpen(false);
        navigateToParent();
    };

    // Return the header component
    return (
        <header className="header-app"> {/* Container for the header */}

            {/* Header top bar with logo */}
            <div className="header-top-bar">
                <div className="scrolling-wrapper">
                    <div className="scrolling-text">{joinedMessages}</div>
                </div>
            </div>

            {/* Back button and page title */}
            <button className="back-button" onClick={handleBack} aria-label="Retour page précédente">
                {location.pathname === '/' ? (
                    <Home /> // Afficher l'icône de la maison si on est à l'accueil
                ) : (
                    <ArrowBack /> // Afficher l'icône de retour si on n'est pas à l'accueil
                )}
                <h1 className="page-title">{title}</h1>
            </button>

            <img 
                className="app-logo" 
                src={logo} 
                alt="Logo de l'application CTR Drako"
            />

            {/* Confirmation modal for back navigation on media pages */}
            <CustomModal
                open={isConfirmBackOpen}
                onClose={() => setIsConfirmBackOpen(false)}
                title="Médias non envoyés"
                handleSave={confirmBack}
                handleSaveLabel="Revenir"
            >
                <InfoBox type="warning" text="Vous avez des médias non envoyés. Êtes-vous sûr de vouloir quitter cette page ?" />
            </CustomModal>
        </header>
    );
};

// Export the Header component as the default export
export default Header;